import { apiGet, apiPatch, apiPost, type ApiClientError } from './client';

export interface EntryNeedingRole {
  id: number;
  userId: number | null;
  projectId: number | null;
  taskId: number | null;
  roleId: number | null;
  date: string | null;
  hours: number;
  description: string | null;
  state: string | null;
  invoiceId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  project: {
    id: number;
    name: string | null;
    fullName: string | null;
  } | null;
  taskRef: {
    id: number;
    name: string;
  } | null;
}

export interface EntriesNeedingRoleResponse {
  data: EntryNeedingRole[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface AdminUpdateEntryRoleInput {
  roleId: number | null;
}

export interface BulkSetRoleInput {
  entryIds: number[];
  roleId: number;
}

// English error-code -> message mapping (memory: feedback_english_only_ui).
// Code resolution checks ApiClientError.details.code (backend emits code there).
const ENTRY_ADMIN_ERROR_MESSAGES: Record<string, string> = {
  entry_admin_invoice_issued:
    'This entry is attached to an issued invoice. Credit-note or unissue the invoice first, then retry.',
  entry_admin_role_required:
    'Role is required on SLA package (over-limit) projects. Pick a role instead of clearing it.',
  entry_admin_role_invalid: 'Selected role does not exist.',
  entry_admin_batch_too_large: 'Batch size exceeds the 200-entry maximum. Split into smaller batches.',
  entry_admin_batch_empty: 'No entries selected.',
  entry_admin_bulk_roleid_required: 'Role is required for bulk update.',
};

/**
 * Extract a readable English message from an ApiClientError thrown by apiPatch/apiPost.
 *
 * ApiClientError shape (from client.ts):
 *   err.message       — top-level error.message from server JSON
 *   err.statusCode    — HTTP status
 *   err.details       — full server error body (parsed JSON), may contain { code, blockedIds, ... }
 *
 * Backend 422 shape (from errorHandler.ts):
 *   { statusCode, error, message, details: { code, blockedIds?, max? } }
 * So err.details === { code, blockedIds?, max? }
 */
export function readableEntryAdminError(err: unknown): string {
  if (err !== null && typeof err === 'object') {
    const apiErr = err as ApiClientError;
    // details is the inner details object: { code?, blockedIds?, max? }
    const details = apiErr.details as { code?: string; blockedIds?: number[]; max?: number } | undefined;
    const code = details?.code;
    if (code && ENTRY_ADMIN_ERROR_MESSAGES[code]) {
      let msg = ENTRY_ADMIN_ERROR_MESSAGES[code];
      if (details?.blockedIds && details.blockedIds.length > 0) {
        msg += ` Blocked entry IDs: ${details.blockedIds.join(', ')}.`;
      }
      return msg;
    }
    if (typeof apiErr.message === 'string' && apiErr.message) {
      return apiErr.message;
    }
  }
  if (err instanceof Error) return err.message;
  return 'Unexpected error.';
}

// ---- Fetch functions ----

export async function getEntriesNeedingRole(params: {
  projectIds?: number[];
  userIds?: number[];
  fromMonth?: string;
  toMonth?: string;
  page?: number;
  pageSize?: number;
}): Promise<EntriesNeedingRoleResponse> {
  const qs = new URLSearchParams();
  if (params.projectIds?.length) qs.set('projectIds', params.projectIds.join(','));
  if (params.userIds?.length) qs.set('userIds', params.userIds.join(','));
  if (params.fromMonth) qs.set('fromMonth', params.fromMonth);
  if (params.toMonth) qs.set('toMonth', params.toMonth);
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiGet<EntriesNeedingRoleResponse>(`/time/admin/entries/needing-role${suffix}`);
}

export async function adminUpdateEntryRole(
  id: number,
  input: AdminUpdateEntryRoleInput,
): Promise<{ data: EntryNeedingRole }> {
  return apiPatch(`/time/admin/entries/${id}/role`, input);
}

export async function bulkSetRole(input: BulkSetRoleInput): Promise<{ data: { updated: number } }> {
  return apiPost('/time/admin/entries/bulk-set-role', input);
}
